import React, { useState, useEffect } from "react";
import {
  Grid,
  IconButton,
  Typography,
  Card,
  CardContent,
  Divider,
  FormHelperText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "./customhours.css";

dayjs.extend(customParseFormat);

const daysOfWeek = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const AddCustomHours = ({
  setCustomHours = () => {},
  setCustomHoursData = () => {},
  initialData = null,
  customHours,
}) => {
  const [hours, setHours] = useState({});
  const [errorMessages, setErrorMessages] = useState(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = [""];
      return acc;
    }, {})
  );

  // Helper function to parse time string to dayjs object
  const parseTimeString = (timeString) => {
    if (!timeString) return null;
    
    try {
      // Try parsing different formats
      const formats = ["hh:mm A", "HH:mm", "h:mm A", "H:mm"];
      
      for (const format of formats) {
        const parsed = dayjs(timeString, format);
        if (parsed.isValid()) {
          return parsed;
        }
      }
      
      // If all formats fail, try default parsing
      const defaultParsed = dayjs(timeString);
      return defaultParsed.isValid() ? defaultParsed : null;
    } catch (error) {
      console.warn("Failed to parse time:", timeString, error);
      return null;
    }
  };

  // Helper function to format time for storage
  const formatTimeForStorage = (dayjsTime) => {
    if (!dayjsTime || !dayjsTime.isValid()) return null;
    return dayjsTime.format("hh:mm A");
  };

  useEffect(() => {
    let configData = [];
    let dataToUse = initialData || customHours;

    if (dataToUse && typeof dataToUse === "object") {
      if (dataToUse.SUNDAY || dataToUse.MONDAY) {
        setHours(dataToUse);
        setCustomHours(dataToUse);
        setCustomHoursData(dataToUse);
        return;
      } else if (Array.isArray(dataToUse)) {
        configData = dataToUse;
      } else if (dataToUse.config && Array.isArray(dataToUse.config)) {
        configData = dataToUse.config;
      } else {
        configData = Object.values(dataToUse)
          .flat()
          .filter((item) => item && typeof item === "object");
      }
    } else {
      // Try to load from localStorage
      try {
        // First try to get the main config
        let storedConfig = localStorage.getItem("config");
        
        if (storedConfig) {
          configData = JSON.parse(storedConfig);
        } else {
          // If no main config, try to get from customHoursConfig keys
          const allKeys = Object.keys(localStorage);
          const customHoursKeys = allKeys.filter(key => key.startsWith('customHoursConfig_'));
          
          configData = [];
          customHoursKeys.forEach(key => {
            try {
              const keyData = JSON.parse(localStorage.getItem(key));
              if (Array.isArray(keyData)) {
                configData = configData.concat(keyData);
              }
            } catch (error) {
              console.warn(`Failed to parse ${key}:`, error);
            }
          });
        }
        
        // If configData is still empty, try customHoursConfig_1 specifically (based on your screenshot)
        if (configData.length === 0) {
          const fallbackData = localStorage.getItem('customHoursConfig_1');
          if (fallbackData) {
            configData = JSON.parse(fallbackData);
          }
        }
        
      } catch (error) {
        console.warn("Failed to parse localStorage config:", error);
        configData = [];
      }
    }

    console.log("Config data loaded:", configData); // Debug log

    const initialHours = daysOfWeek.reduce((acc, day) => {
      const dayConfigs = configData
        .filter((item) => item && item.Day?.toLowerCase() === day.toLowerCase())
        .map((dayConfig) => ({
          start: dayConfig.StartTime || null,
          end: dayConfig.EndTime || null,
        }));

      acc[day] = dayConfigs.length > 0 ? dayConfigs : [{ start: null, end: null }];
      return acc;
    }, {});

    console.log("Initial hours processed:", initialHours); // Debug log

    setHours(initialHours);
    setCustomHours(initialHours);
    setCustomHoursData(initialHours);
  }, []); // Only run once on mount

  const handleTimeChange = (day, index, type, newValue) => {
    const formattedTime = formatTimeForStorage(newValue);
    
    const updatedHours = hours[day].map((time, i) =>
      i === index
        ? {
            ...time,
            [type]: formattedTime,
          }
        : time
    );

    const newHoursState = {
      ...hours,
      [day]: updatedHours,
    };

    setHours(newHoursState);
    setCustomHours(newHoursState);
    setCustomHoursData(newHoursState);

    // Validate time range
    const startTime = updatedHours[index].start;
    const endTime = updatedHours[index].end;

    if (startTime && endTime) {
      const startDayjs = parseTimeString(startTime);
      const endDayjs = parseTimeString(endTime);
      
      if (startDayjs && endDayjs && endDayjs.isBefore(startDayjs)) {
        setErrorMessages((prev) => ({
          ...prev,
          [day]: ["Start time should be less than end time."],
        }));
      } else {
        setErrorMessages((prev) => ({
          ...prev,
          [day]: [""],
        }));
      }
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        [day]: [""],
      }));
    }
  };

  const handleDeleteHour = (day, index) => {
    const updatedHours = hours[day].filter((_, i) => i !== index);
    const newHoursState = {
      ...hours,
      [day]: updatedHours.length > 0 ? updatedHours : [{ start: null, end: null }],
    };

    setHours(newHoursState);
    setCustomHours(newHoursState);
    setCustomHoursData(newHoursState);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card className="custom-hours-card">
        <CardContent>
          {daysOfWeek.map((day, dayIndex) => (
            <React.Fragment key={day}>
              {dayIndex > 0 && <Divider className="day-divider" />}
              <div className="day-row-container">
                <div className="day-info-container">
                  <Typography variant="body1" className="customhourday">
                    {day}
                  </Typography>
                </div>
                <div className="time-picker-container">
                  {hours[day]?.map((time, index) => (
                    <Grid container spacing={0} key={index} className="time-picker-row">
                      <Grid item xs={5} className="time-picker-grid">
                        <TimePicker
                          label="Start time"
                          slotProps={{
                            textField: { variant: "filled", size: "small" },
                          }}
                          value={parseTimeString(time.start)}
                          onChange={(newValue) =>
                            handleTimeChange(day, index, "start", newValue)
                          }
                          className="time-picker"
                          ampm={true}
                        />
                      </Grid>
                      <Grid item xs={5} className="time-picker-grid">
                        <TimePicker
                          label="End time"
                          slotProps={{
                            textField: { variant: "filled", size: "small" },
                          }}
                          value={parseTimeString(time.end)}
                          onChange={(newValue) =>
                            handleTimeChange(day, index, "end", newValue)
                          }
                          className="time-picker"
                          ampm={true}
                        />
                      </Grid>
                      <Grid item xs={1} className="delete-button-grid">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteHour(day, index)}
                          className="delete-button"
                        >
                          <DeleteIcon className="delete-icon" />
                        </IconButton>
                      </Grid>
                      {errorMessages[day][0] && (
                        <Grid item xs={12}>
                          <FormHelperText error>
                            {errorMessages[day][0]}
                          </FormHelperText>
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ))}
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
};

export default AddCustomHours;